import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import type { EmojiCategory } from '../lib/emoji';

/**
 * Пикер эмодзи (дизайн по прототипу владельца): поиск сверху, блок «Недавние»,
 * дальше все категории одной лентой с заголовками — листаешь и находишь нужную.
 *
 * Справочник (`lib/emoji.ts`, ~1900 эмодзи) грузим ДИНАМИЧЕСКИ при первом
 * открытии: иначе он лежал бы в главном бандле и качался всеми, включая тех,
 * кто вики только читает.
 *
 * Клики ловим одним обработчиком на сетке (делегирование) — на 1900 кнопок
 * вешать по паре обработчиков накладно; ховер и вовсе на CSS.
 */
type Loaded = { cats: EmojiCategory[]; keywords: Record<string, string> };

const RECENT_KEY = 'fk.emoji.recent';
const RECENT_MAX = 24;

let cache: Loaded | null = null;

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(emoji: string): string[] {
  const next = [emoji, ...readRecent().filter((e) => e !== emoji)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим — просто не запоминаем */
  }
  return next;
}

export function EmojiPicker({
  onPick,
  header,
  width = 366,
}: {
  onPick: (emoji: string) => void;
  header?: React.ReactNode;
  /** Ширина в px или CSS-значение («100%» — в окне выбора обложки). */
  width?: number | string;
}): JSX.Element {
  const [data, setData] = useState<Loaded | null>(cache);
  const [q, setQ] = useState('');
  const [recent, setRecent] = useState<string[]>(readRecent);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data) return;
    let alive = true;
    void import('../lib/emoji').then((m) => {
      cache = { cats: m.EMOJI_CATEGORIES, keywords: m.EMOJI_KEYWORDS };
      if (alive) setData(cache);
    });
    return () => { alive = false; };
  }, [data]);

  const pick = useCallback((emoji: string) => {
    setRecent(pushRecent(emoji));
    onPick(emoji);
  }, [onPick]);

  // Один обработчик на всю ленту вместо обработчика на каждой кнопке.
  const onGridClick = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
    const el = (ev.target as HTMLElement).closest('[data-e]');
    const emoji = el?.getAttribute('data-e');
    if (emoji) pick(emoji);
  }, [pick]);

  const found = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!data || query.length < 2) return null;
    const out: string[] = [];
    for (const cat of data.cats) {
      if (cat.name === 'Часто используемые') continue;
      for (const e of cat.emojis) {
        if ((data.keywords[e] ?? '').includes(query)) out.push(e);
        if (out.length >= 200) return out;
      }
    }
    return out;
  }, [q, data]);

  const jumpTo = (name: string): void => {
    const box = scrollRef.current;
    const target = box?.querySelector<HTMLElement>(`[data-cat="${CSS.escape(name)}"]`);
    if (box && target) box.scrollTo({ top: target.offsetTop - box.offsetTop, behavior: 'smooth' });
  };

  const cell = (e: string, key: string): JSX.Element => (
    <button key={key} type="button" className="fk-emoji-cell" data-e={e} title={data?.keywords[e] ?? ''}>
      {e}
    </button>
  );

  return (
    <div className="fk-emoji-picker" style={{ width }}>
      {header}
      <div className="fk-emoji-search">
        <SearchOutlined />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Найти эмодзи"
          aria-label="Найти эмодзи"
        />
      </div>

      {!data ? (
        <div style={{ padding: '28px 0', textAlign: 'center' }}>
          <Spin size="small" /> <span style={{ fontSize: 12.5, color: 'var(--fk-text-muted)' }}>Загружаем…</span>
        </div>
      ) : (
        <>
          {!found && (
            <div className="fk-emoji-nav">
              {data.cats.map((cat) => (
                <button key={cat.name} type="button" title={cat.name} onClick={() => jumpTo(cat.name)}>
                  {cat.emojis[0]}
                </button>
              ))}
            </div>
          )}

          <div className="fk-emoji-scroll" ref={scrollRef} onClick={onGridClick}>
            {found ? (
              <>
                <div className="fk-emoji-head">Найдено: {found.length}</div>
                {found.length === 0
                  ? <div className="fk-emoji-empty">Ничего не нашли</div>
                  : <div className="fk-emoji-grid">{found.map((e, i) => cell(e, `f${i}`))}</div>}
              </>
            ) : (
              <>
                <div className="fk-emoji-head" data-cat="Недавние">Недавние</div>
                {recent.length === 0
                  ? <div className="fk-emoji-empty">Вы ещё не выбрали эмодзи</div>
                  : <div className="fk-emoji-grid">{recent.map((e, i) => cell(e, `r${i}`))}</div>}
                {data.cats.map((cat) => (
                  <div key={cat.name}>
                    <div className="fk-emoji-head" data-cat={cat.name}>{cat.name}</div>
                    <div className="fk-emoji-grid">{cat.emojis.map((e, i) => cell(e, `${cat.name}${i}`))}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      <style>{`
        .fk-emoji-picker{display:flex;flex-direction:column}
        .fk-emoji-search{display:flex;align-items:center;gap:8px;padding:8px 12px;margin-bottom:8px;border:1px solid var(--fk-border);border-radius:12px;background:var(--fk-surface);color:var(--fk-text-muted)}
        .fk-emoji-search:focus-within{border-color:var(--fk-orange)}
        .fk-emoji-search input{flex:1;min-width:0;border:none;outline:none;background:transparent;font-family:inherit;font-size:13.5px;color:var(--fk-text)}
        .fk-emoji-nav{display:flex;gap:2px;overflow-x:auto;padding-bottom:6px;border-bottom:1px solid var(--fk-border);margin-bottom:6px}
        .fk-emoji-nav button{flex-shrink:0;border:none;background:transparent;cursor:pointer;border-radius:8px;padding:3px 6px;font-family:inherit;font-size:17px;line-height:1}
        .fk-emoji-nav button:hover{background:var(--fk-orange-soft)}
        .fk-emoji-scroll{max-height:300px;overflow-y:auto;overscroll-behavior:contain}
        .fk-emoji-head{position:sticky;top:0;z-index:1;padding:6px 2px 4px;background:var(--fk-surface);font-size:12px;font-weight:600;color:var(--fk-text-muted)}
        .fk-emoji-empty{padding:10px 2px 14px;text-align:center;font-size:12.5px;color:var(--fk-text-muted)}
        .fk-emoji-grid{display:grid;grid-template-columns:repeat(9,1fr)}
        .fk-emoji-cell{border:none;background:transparent;cursor:pointer;border-radius:8px;padding:4px 0;font-family:inherit;font-size:24px;line-height:1.15}
        .fk-emoji-cell:hover{background:rgba(0,0,0,.06)}
      `}</style>
    </div>
  );
}
